import * as React from "react";
import { NumberField as BaseNumberField } from "@base-ui-components/react/number-field";
import { Minus, MoveHorizontal, Plus } from "lucide-react";
import {
  NumberFieldVariants,
  numberInputClasses,
  numberInputDecrementClasses,
  numberInputFieldGroupClasses,
  numberInputIncrementClasses,
  numberInputLabelClasses,
  numberInputRootClasses,
  numberInputScrubAreaClasses,
  numberInputScrubAreaCursorClasses,
} from "./NumberField.style";

type NumberFieldProps = BaseNumberField.Root.Props &
  NumberFieldVariants & {
    label?: string;
    helpText?: string;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    inputProps?: BaseNumberField.Input.Props;
    onValueChange?: (value: number | null, event?: Event) => void;
    noLabel?: boolean;
  };

function NumberField(props: NumberFieldProps) {
  const {
    label,
    helpText,
    required,
    error,
    id,
    inline,
    className,
    inputProps,
    onValueChange,
    defaultValue = 1,
    noLabel,
    ...rest
  } = props;

  const { className: inputClassName, ...inputRest } = inputProps || {};
  const fieldId = id || React.useId();

  const lastValueRef = React.useRef<number | null>(null);

  type EventDetail = {
    reason: "none";
    event: Event;
    cancel: () => void;
    allowPropagation: () => void;
    isCanceled: boolean;
    isPropagationAllowed: boolean;
  };

  const handleValueChange = React.useCallback(
    (value: number | null, eventDetail?: EventDetail) => {
      if (lastValueRef.current !== value) {
        lastValueRef.current = value;
        if (typeof onValueChange === "function") {
          onValueChange(value, eventDetail?.event);
        }
      }
    },
    [onValueChange]
  );

  React.useEffect(() => {
    lastValueRef.current = defaultValue;
  }, [defaultValue]);

  return (
    <BaseNumberField.Root
      id={fieldId}
      defaultValue={defaultValue}
      className={numberInputRootClasses({
        hasLabel: Boolean(label),
        inline,
        className: typeof className === "string" ? className : "",
      })}
      onValueChange={handleValueChange}
      {...rest}
    >
      <BaseNumberField.ScrubArea className={numberInputScrubAreaClasses()}>
        {label && (
          <label
            htmlFor={fieldId}
            className={numberInputLabelClasses({ noLabel: noLabel })}
          >
            {label}
          </label>
        )}

        <BaseNumberField.ScrubAreaCursor
          className={numberInputScrubAreaCursorClasses()}
        >
          <MoveHorizontal />
        </BaseNumberField.ScrubAreaCursor>
      </BaseNumberField.ScrubArea>

      <BaseNumberField.Group className={numberInputFieldGroupClasses()}>
        <BaseNumberField.Decrement className={numberInputDecrementClasses()}>
          <Minus />
        </BaseNumberField.Decrement>
        <BaseNumberField.Input
          {...inputRest}
          className={numberInputClasses({
            class: typeof inputClassName === "string" ? inputClassName : "",
          })}
        />
        <BaseNumberField.Increment className={numberInputIncrementClasses()}>
          <Plus />
        </BaseNumberField.Increment>
      </BaseNumberField.Group>
    </BaseNumberField.Root>
  );
}

export default NumberField;
